import type { ComponentProps, PropsWithChildren } from "react";
import { t } from "ttag";

import { useScheduleContext } from "@/features/consultations/contexts";
import type { ScheduleAppointment } from "@/features/consultations/models";
import { formatTime } from "@/shared/lib";
import {
  CalendarIcon,
  CircleIcon,
  EmptyState,
  HeartsIcon,
  ScrollArea,
  Skeleton as SkeletonPrimitive,
} from "@/shared/ui";
import { tw } from "@/shared/utils";

const Content = ({ children }: PropsWithChildren) => {
  return (
    <ScrollArea>
      <div className="flex max-h-44 flex-wrap gap-2">{children}</div>
    </ScrollArea>
  );
};

const Disabled = ({
  className,
  ...props
}: ComponentProps<typeof EmptyState.Root>) => {
  return (
    <EmptyState.Root
      className={tw("border-none shadow-sm", className)}
      {...props}
    >
      <CircleIcon icon={CalendarIcon} size="lg" />
      <EmptyState.Description>
        {t`Choose a date to view available times`}
      </EmptyState.Description>
    </EmptyState.Root>
  );
};

const Error = ({
  className,
  ...props
}: ComponentProps<typeof EmptyState.Root>) => {
  return (
    <EmptyState.Root
      className={tw("border-none shadow-sm", className)}
      {...props}
    >
      <EmptyState.Icon>
        <HeartsIcon />
      </EmptyState.Icon>

      <EmptyState.Title>{t`Oops seems like something went wrong`}</EmptyState.Title>

      <EmptyState.Description className="text-base">
        {t`Our team is already working on fixing it.`}
        <br />
        <strong> {t`Try to select another date.`}</strong>
        <br />
        {t`Thank you for your patience!`}
      </EmptyState.Description>
    </EmptyState.Root>
  );
};

const Skeleton = () => {
  return (
    <div className="flex min-h-44 grow flex-col">
      <div className="flex flex-wrap gap-2.5">
        {Array.from({ length: 5 }, (_, index) => `${index}`).map((i) => (
          <SkeletonPrimitive
            key={`time-slot-skeleton-${i}`}
            className="h-9 w-24 rounded-l-full rounded-r-full"
          />
        ))}
      </div>
    </div>
  );
};

interface TimeSlotItemProps extends ComponentProps<"button"> {
  timeSlot: ScheduleAppointment["timeSlot"];
}

const Item = ({ timeSlot, className, ...props }: TimeSlotItemProps) => {
  const {
    scheduleDate,
    actions: { setScheduleDate },
  } = useScheduleContext();

  return (
    <button
      {...props}
      type="button"
      className={tw(
        "h-fit w-24 rounded-l-full rounded-r-full bg-white py-2 text-center text-sm font-medium text-secondary-950 duration-150 hover:bg-secondary-200 hover:text-secondary-950",
        className,
        timeSlot.dateTime === scheduleDate.timeSlot?.dateTime &&
          "bg-secondary-950 text-secondary-50",
      )}
      onClick={() => setScheduleDate((prev) => ({ ...prev, timeSlot }))}
    >
      {`${formatTime(timeSlot.dateTime)}`}
    </button>
  );
};

export { Content, Disabled, Error, Skeleton, Item };
